import { useCallback, useEffect, useState } from "react";
import { fetchEmailTemplates } from "@/api/email";
import { useAuth } from "@/lib/authContext";
import type { EmailTemplate, EmailTemplateOption } from "@/types/email";

const toOption = (template: EmailTemplate): EmailTemplateOption => ({
  id: template.id,
  template_id: template.id,
  version_id: template.id,
  name: template.name,
  subject: template.subject,
  body_html: template.body_html,
  variant: template.variant ?? null,
  channel: null,
  persona: null,
  metadata: null,
  active: true,
});

export function useEmailTemplates() {
  const [templates, setTemplates] = useState<EmailTemplateOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { isAuthenticated } = useAuth();

  const refresh = useCallback(async () => {
    if (!isAuthenticated) {
      setTemplates([]);
      setError(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const payload = await fetchEmailTemplates();
      setTemplates((payload ?? []).map(toOption));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load templates");
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { templates, loading, error, refresh };
}
